import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@components/ui/Card';
import Input from '@components/ui/Input';
import Button from '@components/ui/Button';
import Badge from '@components/ui/Badge';
import { 
  Search, 
  Mail, 
  Users, 
  Calendar,
  Check,
  X,
  ExternalLink,
  FileText
} from 'lucide-react';
import type { Booking } from '../../types';
import { upcomingEvents } from '@data/siteData';

// Mock bookings data
const mockBookings: Booking[] = [
  {
    id: '1',
    eventId: '1',
    name: 'Max Mustermann',
    email: 'max.mustermann@example.com',
    participants: 2,
    requirements: 'Einer der Teilnehmer hat Mobilitätseinschränkungen.',
    status: 'confirmed',
    bookingReference: 'BK-2026-001',
    createdAt: '2026-01-15T10:30:00Z',
  },
  {
    id: '2',
    eventId: '2',
    name: 'Anna Schmidt',
    email: 'anna.schmidt@example.com',
    participants: 4,
    status: 'pending',
    bookingReference: 'BK-2026-002',
    createdAt: '2026-01-18T14:20:00Z',
  },
  {
    id: '3',
    eventId: '3',
    name: 'Peter Müller',
    email: 'peter.mueller@example.com',
    participants: 1,
    status: 'confirmed',
    bookingReference: 'BK-2026-003',
    createdAt: '2026-01-20T09:15:00Z',
  },
  {
    id: '4',
    eventId: '1',
    name: 'Lisa Weber',
    email: 'lisa.weber@example.com',
    participants: 3,
    requirements: 'Vegetarische Verpflegung erwünscht',
    status: 'cancelled',
    bookingReference: 'BK-2026-004',
    createdAt: '2026-01-22T16:45:00Z',
  },
  {
    id: '5',
    eventId: '4',
    name: 'Thomas Klein',
    email: 'thomas.klein@example.com',
    participants: 2,
    status: 'pending',
    bookingReference: 'BK-2026-005',
    createdAt: '2026-01-25T11:30:00Z',
  },
  {
    id: '6',
    eventId: '2',
    name: 'Sarah Groß',
    email: 'sarah.gross@example.com',
    participants: 5,
    status: 'confirmed',
    bookingReference: 'BK-2026-006',
    createdAt: '2026-01-28T13:00:00Z',
  },
];

type FilterStatus = 'all' | 'confirmed' | 'pending' | 'cancelled';

export default function BookingsManage() {
  const [bookings, setBookings] = useState<Booking[]>(mockBookings);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<FilterStatus>('all');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const filteredBookings = useMemo(() => {
    return bookings.filter(booking => {
      const matchesSearch = 
        booking.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        booking.bookingReference.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || booking.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [bookings, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    return {
      total: bookings.length,
      confirmed: bookings.filter(b => b.status === 'confirmed').length,
      pending: bookings.filter(b => b.status === 'pending').length,
      cancelled: bookings.filter(b => b.status === 'cancelled').length,
      totalParticipants: bookings
        .filter(b => b.status !== 'cancelled')
        .reduce((sum, b) => sum + b.participants, 0),
    };
  }, [bookings]);

  const updateStatus = (id: string, status: Booking['status']) => {
    setBookings(bookings.map(b => 
      b.id === id ? { ...b, status } : b
    ));
  };

  const getEventTitle = (eventId: string) => {
    const event = upcomingEvents.find(e => e.id === eventId);
    return event?.title || 'Unbekannte Veranstaltung';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return <Badge variant="success">Bestätigt</Badge>;
      case 'pending':
        return <Badge variant="warning">Ausstehend</Badge>;
      case 'cancelled':
        return <Badge variant="error">Storniert</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filterButtons: { key: FilterStatus; label: string; count: number }[] = [
    { key: 'all', label: 'Alle', count: stats.total },
    { key: 'confirmed', label: 'Bestätigt', count: stats.confirmed },
    { key: 'pending', label: 'Ausstehend', count: stats.pending },
    { key: 'cancelled', label: 'Storniert', count: stats.cancelled },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Buchungen</h1>
        <p className="text-slate-400 mt-1">
          Verwalten Sie Anmeldungen zu Veranstaltungen
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Gesamt</p>
            <p className="text-2xl font-bold text-white">{stats.total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Bestätigt</p>
            <p className="text-2xl font-bold text-green-400">{stats.confirmed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Ausstehend</p>
            <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-slate-400 text-sm">Teilnehmer</p>
            <p className="text-2xl font-bold text-blue-400">{stats.totalParticipants}</p>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
          <Input
            placeholder="Name, E-Mail oder Buchungsnummer suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12"
          />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {filterButtons.map((filter) => (
          <button
            key={filter.key}
            onClick={() => setStatusFilter(filter.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              statusFilter === filter.key
                ? 'bg-blue-600 text-white'
                : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'
            }`}
          >
            {filter.label}
            <span className="ml-2 text-xs opacity-70">({filter.count})</span>
          </button>
        ))}
      </div>

      {/* Bookings List */}
      <Card>
        <CardContent className="p-0">
          {filteredBookings.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">
                Keine Buchungen gefunden
              </h3>
              <p className="text-slate-400">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Passen Sie Ihre Filter an.' 
                  : 'Es gibt noch keine Buchungen.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-800">
              {filteredBookings.map((booking) => (
                <div 
                  key={booking.id} 
                  className="px-6 py-4 hover:bg-slate-900/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-medium">{booking.name}</h3>
                        {getStatusBadge(booking.status)}
                      </div>
                      <p className="text-slate-400 text-sm mb-2">
                        {getEventTitle(booking.eventId)}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" />
                          {booking.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" />
                          {booking.participants} Teilnehmer
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(booking.createdAt)}
                        </span>
                      </div>
                      {booking.requirements && (
                        <p className="text-slate-500 text-sm mt-2 italic">
                          „{booking.requirements}"
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {booking.status === 'pending' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateStatus(booking.id, 'confirmed')}
                            className="text-green-400 hover:text-green-300 hover:bg-green-900/20"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => updateStatus(booking.id, 'cancelled')}
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedBooking(booking)}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedBooking(null)}
        >
          <div 
            className="w-full max-w-lg"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Buchungsdetails</CardTitle>
                <CardDescription>{selectedBooking.bookingReference}</CardDescription>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedBooking(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-slate-400 text-sm">Name</p>
                  <p className="text-white">{selectedBooking.name}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">E-Mail</p>
                  <p className="text-white">{selectedBooking.email}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Teilnehmer</p>
                  <p className="text-white">{selectedBooking.participants}</p>
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedBooking.status)}</div>
                </div>
              </div>
              <div>
                <p className="text-slate-400 text-sm">Veranstaltung</p>
                <p className="text-white">{getEventTitle(selectedBooking.eventId)}</p>
              </div>
              {selectedBooking.requirements && (
                <div>
                  <p className="text-slate-400 text-sm">Anforderungen</p>
                  <p className="text-white bg-slate-900 p-3 rounded-lg mt-1">
                    {selectedBooking.requirements}
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                {selectedBooking.status === 'pending' && (
                  <>
                    <Button 
                      className="flex-1"
                      onClick={() => {
                        updateStatus(selectedBooking.id, 'confirmed');
                        setSelectedBooking(null);
                      }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Bestätigen
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 border-red-600 text-red-400 hover:bg-red-900/20"
                      onClick={() => {
                        updateStatus(selectedBooking.id, 'cancelled');
                        setSelectedBooking(null);
                      }}
                    >
                      <X className="w-4 h-4 mr-2" />
                      Ablehnen
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
